// src/components/MaintenanceLists/MaintenanceRecords.js
import React from 'react';
import { Button } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPenToSquare, faTrashCan } from '@fortawesome/free-solid-svg-icons';
import FormattedDate from '../FormattedDate';
import { formatCost, getSubcategoryName, MAINTENANCE_CATEGORIES } from './constants';
import { useTheme } from '../../utils/ThemeContext';

/**
 * MaintenanceRecords component for displaying maintenance records table
 * 
 * @param {Object} props - Component props
 * @param {Array} props.maintenanceItems - Array of maintenance items
 * @param {boolean} props.isOwner - Whether the current user is the owner
 * @param {string} props.sortOrder - Sort order ('newest' or 'oldest')
 * @param {Function} props.onSortOrderChange - Function to change sort order
 * @param {Function} props.onEditItem - Function to edit an item
 * @param {Function} props.onDeleteItem - Function to delete an item
 * @param {Function} props.onAddMaintenanceClick - Function for add maintenance button
 * @param {Function} props.onAddObservationClick - Function for add observation button
 * @param {string|null} props.expandedRowId - ID of expanded row for notes
 * @param {Function} props.onToggleExpand - Function to toggle row expansion
 * @returns {JSX.Element} Rendered component
 */
const MaintenanceRecords = ({
  maintenanceItems,
  isOwner,
  sortOrder,
  onSortOrderChange,
  onEditItem,
  onDeleteItem,
  onAddMaintenanceClick,
  onAddObservationClick,
  expandedRowId,
  onToggleExpand
}) => {
  const { theme } = useTheme();
  return (
    <div className="maintenance-section">
      <div className="maintenance-header">
        <h2>Maintenance Records</h2>
        
        <div className="maintenance-controls">
          {isOwner && (
            <>
              <Button 
                variant="info" 
                onClick={onAddObservationClick}
              >
                Add Observation Record
              </Button>
              <Button 
                variant="success" 
                onClick={onAddMaintenanceClick}
              >
                Add Maintenance Record
              </Button>
            </>
          )}
          
          <div className="sort-controls">
            <label htmlFor="sort-order">Sort:</label>
            <select 
              id="sort-order"
              value={sortOrder}
              onChange={(e) => onSortOrderChange(e.target.value)}
            >
              <option value="newest">Newest First</option>
              <option value="oldest">Oldest First</option>
            </select>
          </div>
        </div>
      </div>
      
      <div className="maintenance-records">
        {maintenanceItems.length > 0 ? (
          <div className="records-list">
            <table className="maintenance-table">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Category</th>
                  <th>Description</th>
                  <th>Odometer</th>
                  <th>Cost</th>
                  <th>Location</th>
                  <th>DIY</th>
                  {isOwner && <th>Actions</th>}
                </tr>
              </thead>
              <tbody>
                {maintenanceItems.map(item => {
                  const isObservation = item.type === 'observation';
                  const hasNotes = item.notes && item.notes.trim().length > 0;
                  return (
                    <React.Fragment key={item.id}>
                      <tr 
                        onClick={() => {
                          if (hasNotes) {
                            onToggleExpand(item.id);
                          } else if (isOwner) {
                            onEditItem(item);
                          }
                        }} 
                        className={`${isOwner || hasNotes ? 'clickable' : ''} ${expandedRowId === item.id ? 'expanded' : ''}`}
                      >
                        <td className="date-cell" data-label="Date">
                          <FormattedDate date={item.date} />
                        </td>
                        <td className="category-cell" data-label="Category">
                          <div className="category-info">
                            {isObservation ? (
                              <span className="category-badge observation">Observation</span>
                            ) : (
                              <>
                                <span className={`category-badge ${item.category}`}>
                                  {MAINTENANCE_CATEGORIES[item.category] || item.category}
                                </span>
                                {item.subcategory && (
                                  <span className={`subcategory-name subcategory-${item.category}`}>
                                    {getSubcategoryName(item.category, item.subcategory)}
                                  </span>
                                )}
                              </>
                            )}
                          </div>
                        </td>
                        <td className="description-cell" data-label="Description">{item.description}</td>
                        <td className="odometer-cell" data-label="Odometer">
                          {item.odometer && `${item.odometer.toLocaleString()} mi`}
                        </td>
                        {isObservation ? (
                          <td colSpan="3" className="observation-status-cell" data-label="Status">
                            <div className="observation-status">
                              <span className={`info-pill severity-${item.severity}`}>
                                <span className="pill-label">Severity</span>
                                <span className="pill-value">{item.severity.charAt(0).toUpperCase() + item.severity.slice(1)}</span>
                              </span>
                              <span className={`info-pill status-${item.status}`}>
                                <span className="pill-label">Status</span>
                                <span className="pill-value">{item.status.charAt(0).toUpperCase() + item.status.slice(1)}</span>
                              </span>
                            </div>
                          </td>
                        ) : (
                          <>
                            <td className="cost-cell" data-label="Cost">{item.cost !== null && formatCost(item.cost)}</td>
                            <td className="location-cell" data-label="Location">{item.location}</td>
                            <td className="diy-cell" data-label="DIY">
                              {item.isDIY && <span className="diy-badge">DIY</span>}
                            </td>
                          </>
                        )}
                        {isOwner && (
                          <td className="actions-cell">
                            <Button 
                              variant={theme === 'dark' ? "outline-light" : "outline-dark"}
                              className="edit-maintenance-button" 
                              onClick={(e) => {
                                e.stopPropagation();
                                onEditItem(item);
                              }}
                              title="Edit"
                              size="sm"
                            >
                              <FontAwesomeIcon icon={faPenToSquare} />
                            </Button>
                            <Button 
                              variant={theme === 'dark' ? "outline-light" : "outline-dark"}
                              className="delete-maintenance-button"
                              onClick={(e) => {
                                e.stopPropagation();
                                onDeleteItem(item.id);
                              }}
                              title="Delete"
                              size="sm"
                            >
                              <FontAwesomeIcon icon={faTrashCan} />
                            </Button>
                          </td>
                        )}
                      </tr>
                      {hasNotes && (
                        <tr className={`notes-row ${expandedRowId === item.id ? 'expanded' : ''}`}>
                          <td colSpan={isOwner ? 8 : 7} className="notes-cell">
                            {item.notes}
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="empty-records">
            <p>No maintenance records yet.</p>
            {isOwner && (
              <p>Add your first maintenance record to start tracking your motorcycle's history.</p>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default MaintenanceRecords;